import React, { useState, useEffect } from 'react';
import HeaderPage from '../components/HeaderPage';
import { useBlogContext } from '../../fetchdata/BlogContext';

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Format "09:00" → "9:00 AM"
const formatTime = (time) => {
  if (!time) return "";
  const [hourStr, minute] = time.split(":");
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${ampm}`;
};

const getTodayName = () =>
  ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][
  new Date().getDay()
  ];

// Simple Skeleton Loader
const Skeleton = () => (
  <div className="p-3">
    <div className="skeleton mb-3" style={{ height: 200 }} />
    <div className="skeleton w-50 mb-2"></div>
    <div className="skeleton w-75 mb-2"></div>
    <div className="skeleton w-25 mb-2"></div>
    <div className="skeleton w-50"></div>
  </div>
);

const StoreLocation = () => {
  const { Headers } = useBlogContext();
  const [loading, setLoading] = useState(true);
  const [showAllDays, setShowAllDays] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const shopTimings = Headers?.shopTimings || {};
  const today = getTodayName();
  const todayTiming = shopTimings[today];

  const isOpenNow = () => {
    if (!todayTiming || todayTiming.isOff) return false;
    const now = new Date();
    const [openH, openM] = todayTiming.open.split(":").map(Number);
    const [closeH, closeM] = todayTiming.close.split(":").map(Number);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
  };

  const openNow = isOpenNow();

  return (
    <>
      <HeaderPage title={"Store Locator"} />

      <main className="page-content bg-light pb-5" style={{ minHeight: "100vh" }}>
        {loading ? (
          <Skeleton />
        ) : (
          <div className="container py-3" style={{ maxWidth: "600px" }}>

            {/* Map Section */}
            <div className="mb-3" style={{ borderRadius: "10px", overflow: "hidden" }}>
              <iframe
                title="store-map"
                src={`https://www.google.com/maps?q=${Headers.address}&output=embed`}
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>

            {/* Store Info */}
            <div className="bg-white p-3 rounded shadow-sm mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">{Headers.address}</h6>
                <button className="btn btn-sm btn-outline-dark rounded-pill px-3">
                  <i className="ri-navigation-fill me-2"></i>Direction
                </button>
              </div>

              <div className="d-flex">
                <div className="col">
                  <a href={`tel:${Headers?.phone || ""}`} className="text-decoration-none text-dark small">
                    <i className="ri-phone-fill text-primary me-2"></i>
                    {Headers?.phone || ""}
                  </a>
                </div>
                <div className="col">
                  <a href={`mailto:${Headers?.email || ""}`} className="text-decoration-none text-dark small">
                    <i className="ri-mail-fill text-primary me-2"></i>
                    {Headers?.email || ""}
                  </a>
                </div>
              </div>
            </div>

            {/* Shop Timings */}
            <div className="bg-white rounded shadow-sm overflow-hidden">

              {/* Header row — today's status */}
              <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                style={{ cursor: "pointer" }}
                onClick={() => setShowAllDays(!showAllDays)}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-time-line text-primary me-1"></i>
                  <span className="fw-semibold small">
                    {todayTiming?.isOff
                      ? "Closed Today"
                      : `${formatTime(todayTiming?.open)} – ${formatTime(todayTiming?.close)}`}
                  </span>
                  <span className={`badge rounded-pill ms-1 ${openNow ? "bg-success" : "bg-danger"}`}
                    style={{ fontSize: "10px" }}>
                    {openNow ? "Open Now" : "Closed"}
                  </span>
                </div>
                <i className={`ri-arrow-${showAllDays ? "up" : "down"}-s-line text-muted`}></i>
              </div>

              {/* All days list */}
              {showAllDays && (
                <ul className="list-unstyled mb-0">
                  {DAYS.map((day, i) => {
                    const timing = shopTimings[day];
                    const isToday = day === today;
                    return (
                      <li
                        key={day}
                        className={`d-flex justify-content-between align-items-center px-3 py-2 small
                          ${i !== DAYS.length - 1 ? "border-bottom" : ""}
                          ${isToday ? "bg-light" : ""}
                        `}
                      >
                        <span className={`${isToday ? "fw-bold text-primary" : "text-dark"}`}>
                          {isToday ? `${day} (Today)` : day}
                        </span>
                        {timing?.isOff ? (
                          <span className="text-danger fw-semibold">Holiday</span>
                        ) : (
                          <span className="text-muted">
                            {formatTime(timing?.open)} – {formatTime(timing?.close)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

          </div>
        )}
      </main>
    </>
  );
};

export default StoreLocation;